import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import { toast } from 'react-toastify';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

export default function CustomerNew() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [form, setForm] = useState({
        consumerNumber: '',
        name: '',
        email: '',
        mobile: '',
        address: ''
    });

    const createCustomer = useMutation(
        (data) => api.post('/customers', data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('customers');
                toast.success('Customer created successfully');
                navigate('/customers');
            },
            onError: (err) => {
                toast.error(err.response?.data?.message || 'Failed to create customer');
            }
        }
    );

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!form.consumerNumber || !form.name) {
            toast.error('Consumer Number and Name are required');
            return;
        }

        const consumerRegex = /^[a-zA-Z0-9\s\-_@]+$/;
        const nameRegex = /^[A-Za-z\s]+$/;

        if (!consumerRegex.test(form.consumerNumber)) {
            toast.error('Consumer Number can only contain alphanumeric characters, spaces, and -_@');
            return;
        }

        if (!nameRegex.test(form.name)) {
            toast.error('Full Name can only contain alphabets and spaces');
            return;
        }

        createCustomer.mutate(form);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'name' && value && !/^[A-Za-z\s]*$/.test(value)) {
            return;
        }
        if (name === 'consumerNumber' && value && !/^[a-zA-Z0-9\s\-_@]*$/.test(value)) {
            return;
        }

        setForm({ ...form, [name]: value });
    };

    const handleNameKeyDown = (e) => {
        // Allow backspace, delete, escape, and other control keys
        const allowedKeys = ['Backspace', 'Delete', 'Escape', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter'];
        if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey || e.altKey) {
            return;
        }
        // Block explicitly invalid keys
        if (!/^[A-Za-z\s]$/.test(e.key)) {
            e.preventDefault();
        }
    };

    const handleConsumerKeyDown = (e) => {
        // Allow backspace, delete, escape, and other control keys
        const allowedKeys = ['Backspace', 'Delete', 'Escape', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter'];
        if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey || e.altKey) {
            return;
        }
        // Block explicitly invalid keys
        if (!/^[a-zA-Z0-9\s\-_@]$/.test(e.key)) {
            e.preventDefault();
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <button onClick={() => navigate('/customers')} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                    <ArrowLeftIcon className="h-5 w-5 text-slate-600" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Add New Customer</h1>
                    <p className="text-sm text-slate-500">Register a new customer account in the system</p>
                </div>
            </div>

            <div className="card max-w-2xl">
                <div className="card-header">
                    <h3 className="text-lg font-medium text-slate-900">Customer Details</h3>
                </div>
                <form onSubmit={handleSubmit} className="card-body space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Consumer Number <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="consumerNumber"
                            value={form.consumerNumber}
                            onChange={handleChange}
                            onKeyDown={handleConsumerKeyDown}
                            placeholder="e.g. CON-10002"
                            className="input-field"
                            pattern="^[a-zA-Z0-9\s\-_@]+$"
                            title="Alphanumeric, space, and -_@ are allowed"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            onKeyDown={handleNameKeyDown}
                            placeholder="e.g. John Doe"
                            className="input-field"
                            pattern="^[A-Za-z\s]+$"
                            title="Only alphabets and spaces are allowed"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Email Address
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder="e.g. john@example.com"
                            className="input-field"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Mobile Number
                        </label>
                        <input
                            type="text"
                            name="mobile"
                            value={form.mobile}
                            onChange={handleChange}
                            placeholder="e.g. +923001234567"
                            className="input-field"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Address
                        </label>
                        <textarea
                            name="address"
                            value={form.address}
                            onChange={handleChange}
                            placeholder="Street address, city"
                            rows="3"
                            className="input-field"
                        />
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                        <button
                            type="button"
                            onClick={() => navigate('/customers')}
                            className="btn-secondary"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={createCustomer.isLoading}
                            className="btn-primary"
                        >
                            {createCustomer.isLoading ? 'Creating...' : 'Register Customer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
